// pathfinder.js — the "GPS" logic.
// Given one or more goal nodes, trace prerequisite chains BACKWARD to find
// everything a learner needs, then order it so prerequisites come before dependents.

// Reads from the MERGED registry so frontier nodes/edges participate too.
// Core routes never pull in frontier nodes (no frontier node is a prereq of a
// core node), so the academic base stays clean whether or not the frontier is on.
import { ALL_NODES as NODES, ALL_PREREQS as PREREQS, ALL_CROSS as CROSS } from './registry.js';

const byId = Object.fromEntries(NODES.map(n => [n.id, n]));

// Adjacency: for a target node, what are its direct prerequisites?
const prereqsOf = {};   // node -> [{from, strength}]
const dependentsOf = {}; // node -> [to]
NODES.forEach(n => { prereqsOf[n.id] = []; dependentsOf[n.id] = []; });
PREREQS.forEach(([from, to, strength]) => {
  prereqsOf[to].push({ from, strength });
  dependentsOf[from].push(to);
});

// Cross-domain neighbours of a node (both layers).
export function crossLinksOf(id) {
  const out = [];
  CROSS.forEach(c => {
    if (c.a === id) out.push(c.b);
    else if (c.b === id) out.push(c.a);
  });
  return out;
}

// Compute the full prerequisite closure of a set of goal ids (the goals included),
// then return a topologically ordered list. Each entry carries:
//   { id, depth, isGoal, hardOnly } where hardOnly indicates whether at least
//   one incoming prereq inside the path is "soft" (skippable for a fast track).
export function buildPath(goalIds) {
  const goals = new Set(goalIds.filter(id => byId[id]));
  if (goals.size === 0) return { order: [], goals: [] };

  // 1. Collect closure via DFS backward over prerequisites.
  const inPath = new Set();
  const stack = [...goals];
  while (stack.length) {
    const cur = stack.pop();
    if (inPath.has(cur)) continue;
    inPath.add(cur);
    prereqsOf[cur].forEach(p => { if (!inPath.has(p.from)) stack.push(p.from); });
  }

  // 2. Topological sort (Kahn) restricted to nodes in the path.
  const indeg = {};
  inPath.forEach(id => { indeg[id] = 0; });
  inPath.forEach(id => {
    prereqsOf[id].forEach(p => { if (inPath.has(p.from)) indeg[id]++; });
  });
  // Queue starts with zero-indegree nodes; tie-break alphabetically by name for stability.
  const ready = [...inPath].filter(id => indeg[id] === 0)
    .sort((a, b) => byId[a].name.localeCompare(byId[b].name));
  const order = [];
  const depth = {};
  inPath.forEach(id => { depth[id] = 0; });
  while (ready.length) {
    const cur = ready.shift();
    order.push(cur);
    dependentsOf[cur].forEach(to => {
      if (!inPath.has(to)) return;
      depth[to] = Math.max(depth[to], depth[cur] + 1);
      if (--indeg[to] === 0) {
        // insert keeping alpha order among currently-ready
        let i = 0;
        while (i < ready.length && byId[ready[i]].name.localeCompare(byId[to].name) < 0) i++;
        ready.splice(i, 0, to);
      }
    });
  }

  // 3. Determine the "fast track": the subset reachable using only HARD prereqs.
  const hardReach = new Set(goals);
  const hstack = [...goals];
  while (hstack.length) {
    const cur = hstack.pop();
    prereqsOf[cur].forEach(p => {
      if (p.strength === 'hard' && !hardReach.has(p.from)) {
        hardReach.add(p.from); hstack.push(p.from);
      }
    });
  }

  const entries = order.map(id => ({
    id,
    node: byId[id],
    depth: depth[id],
    isGoal: goals.has(id),
    essential: hardReach.has(id), // on the hard-prereq fast track
  }));

  return {
    order: entries,
    goals: [...goals].map(id => byId[id]),
    counts: {
      total: entries.length,
      essential: entries.filter(e => e.essential).length,
      optional: entries.filter(e => !e.essential).length,
    },
  };
}

// For a single node, return its direct neighbourhood for the inspector.
export function neighborhood(id) {
  const node = byId[id];
  const prerequisites = prereqsOf[id].map(p => ({ node: byId[p.from], strength: p.strength }));
  const unlocks = dependentsOf[id].map(to => byId[to]);
  const cross = crossLinksOf(id).map(c => byId[c]);
  return { node, prerequisites, unlocks, cross };
}

export { byId };
